import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpHeaders, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';

export interface PendingUpload {
  file: File;
  audioUploadUrl: string;
}

@Injectable({
  providedIn: 'root'
})
export class UploadService {
  private pendingUploads = new Map<string, PendingUpload>();

  constructor(private http: HttpClient) {}

  /**
   * Holds the selected file across the /upload → /upload/:id navigation so the
   * upload can start on the routed page without asking the user to re-select.
   */
  setPendingUpload(dictationId: string, file: File, audioUploadUrl: string): void {
    this.pendingUploads.set(dictationId, { file, audioUploadUrl });
  }

  consumePendingUpload(dictationId: string): PendingUpload | null {
    const pending = this.pendingUploads.get(dictationId) ?? null;
    if (pending) {
      this.pendingUploads.delete(dictationId);
    }
    return pending;
  }

  clearPendingUpload(dictationId: string): void {
    this.pendingUploads.delete(dictationId);
  }

  /**
   * Uploads a file to a pre-signed S3 PUT URL with application/octet-stream content-type.
   * This is required by S3 pre-signed PUT URLs generated in nlp-micro which are signed with application/octet-stream.
   * @param url The pre-signed upload URL.
   * @param file The file to upload.
   * @param contentType The content type. Default is 'application/octet-stream' to match S3 signature.
   */
  uploadToPresignedUrl(url: string, file: File, contentType: string = 'application/octet-stream'): Observable<HttpEvent<any>> {
    const headers = new HttpHeaders({ 'Content-Type': contentType });
    const req = new HttpRequest('PUT', url, file, {
      headers,
      reportProgress: true
    });
    return this.http.request(req);
  }
}
